import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Icon from '../../common/Icons';
import notificationService from '../../../services/notificationService';
import { useNotificationSummary } from '../../../hooks/useNotificationSummary';
import { AdminPersonnelPageHeader } from '../ui';
import { useApToast } from '../hooks/useApToast';
import { AP_COPY } from '../adminPersonnelCopy';
import {
  mapNotificationFromApi,
  formatNotificationTime,
  getNotificationIcon,
  getNotificationNavigateTarget,
  getNotificationActionLabel,
  NOTIF_API_TYPES
} from '../utils/notificationMappers';
import '../AdminPersonnelPortal.css';
import './AdminPersonnelNotificationsPage.css';

const PAGE_SIZE = 15;

const READ_FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'unread', label: 'Non lues' }
];

const TYPE_FILTERS = [
  { id: 'all', label: 'Tous types' },
  { id: 'ALERT', label: 'Alertes' },
  { id: 'WARNING', label: 'Avertissements' },
  { id: 'SUCCESS', label: 'Succès' },
  { id: 'INFO', label: 'Infos' }
];

export default function AdminPersonnelNotificationsPage() {
  const navigate = useNavigate();
  const { showToast } = useApToast();
  const { summary, refresh: refreshSummary } = useNotificationSummary({ pollMs: 0 });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingRead, setDeletingRead] = useState(false);
  const [readFilter, setReadFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const offset = (page - 1) * PAGE_SIZE;

  const loadNotifications = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        const response = await notificationService.list(
          {
            limit: PAGE_SIZE,
            offset,
            unreadOnly: readFilter === 'unread',
            type: typeFilter !== 'all' ? typeFilter : undefined
          },
          { signal }
        );
        const rows = response?.data || [];
        setItems(rows.map(mapNotificationFromApi));
        setTotal(response?.pagination?.total ?? rows.length);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Chargement notifications:', error);
        showToast('Impossible de charger les notifications', 'danger');
        setItems([]);
        setTotal(0);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [offset, readFilter, typeFilter, showToast]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadNotifications(controller.signal);
    return () => controller.abort();
  }, [loadNotifications]);

  useEffect(() => {
    setPage(1);
  }, [readFilter, typeFilter]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      refreshSummary();
    } catch (error) {
      showToast(error.message || 'Erreur lors du marquage', 'danger');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (summary.unread === 0) return;
    try {
      setMarkingAll(true);
      await notificationService.markAllAsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      refreshSummary();
      showToast('Toutes les notifications ont été marquées comme lues', 'success');
    } catch (error) {
      showToast(error.message || 'Erreur lors du marquage groupé', 'danger');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleRefresh = () => {
    const controller = new AbortController();
    loadNotifications(controller.signal);
    refreshSummary();
  };

  const reloadList = useCallback(() => {
    const controller = new AbortController();
    loadNotifications(controller.signal);
    refreshSummary();
  }, [loadNotifications, refreshSummary]);

  const handleDelete = async (id) => {
    if (!window.confirm(N.CONFIRM_DELETE)) return;
    try {
      setDeletingId(id);
      await notificationService.delete(id);
      showToast(N.DELETE_SUCCESS, 'success');
      if (items.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        setItems((prev) => prev.filter((n) => n.id !== id));
        setTotal((t) => Math.max(0, t - 1));
        refreshSummary();
      }
    } catch (error) {
      showToast(error.message || 'Erreur lors de la suppression', 'danger');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAllRead = async () => {
    const readCount = summary.read;
    if (readCount === 0) return;
    if (!window.confirm(N.CONFIRM_DELETE_READ)) return;
    try {
      setDeletingRead(true);
      const response = await notificationService.deleteAllRead();
      const deleted = response?.data?.deleted ?? 0;
      showToast(N.DELETE_READ_SUCCESS(deleted), deleted > 0 ? 'success' : 'info');
      setPage(1);
      reloadList();
    } catch (error) {
      showToast(error.message || 'Erreur lors de la suppression', 'danger');
    } finally {
      setDeletingRead(false);
    }
  };

  const N = AP_COPY.NOTIFICATIONS;

  return (
    <div className="ap-dash ap-notif-page">
      <AdminPersonnelPageHeader
        badge={AP_COPY.DASHBOARD.BADGE}
        title={N.PAGE_TITLE}
        description={N.PAGE_SUBTITLE}
        actions={
          <div className="ap-notif-header-actions">
            <button
              type="button"
              className="ap-notif-btn ap-notif-btn--ghost"
              onClick={handleRefresh}
              disabled={loading}
            >
              <Icon name="refresh" size={14} />
              {N.BTN_REFRESH}
            </button>
            <button
              type="button"
              className="ap-notif-btn ap-notif-btn--danger"
              onClick={handleDeleteAllRead}
              disabled={deletingRead || summary.read === 0}
            >
              {deletingRead ? (
                <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
              ) : (
                <Icon name="trash" size={14} />
              )}
              {N.BTN_DELETE_READ}
            </button>
            <button
              type="button"
              className="ap-notif-btn ap-notif-btn--primary"
              onClick={handleMarkAllAsRead}
              disabled={markingAll || summary.unread === 0}
            >
              {markingAll ? (
                <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
              ) : (
                <Icon name="checkCircle" size={14} />
              )}
              {N.BTN_MARK_ALL}
            </button>
          </div>
        }
      />

      <section className="ap-shell">
        <div className="ap-notif-stats">
          <div className="ap-notif-stat">
            <span className="ap-notif-stat__value">{summary.total}</span>
            <span className="ap-notif-stat__label">{N.STAT_TOTAL}</span>
          </div>
          <div className="ap-notif-stat ap-notif-stat--accent">
            <span className="ap-notif-stat__value">{summary.unread}</span>
            <span className="ap-notif-stat__label">{N.STAT_UNREAD}</span>
          </div>
          {NOTIF_API_TYPES.map((type) => (
            <div key={type} className={`ap-notif-stat ap-notif-stat--${type.toLowerCase()}`}>
              <span className="ap-notif-stat__value">{summary.byType?.[type] ?? 0}</span>
              <span className="ap-notif-stat__label">{type}</span>
            </div>
          ))}
        </div>

        <div className="ap-toolbar ap-notif-toolbar">
          <div className="ap-notif-filters" role="group" aria-label="Filtrer par lecture">
            {READ_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`ap-notif-filter-pill ${readFilter === f.id ? 'is-active' : ''}`}
                onClick={() => setReadFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            className="form-select ap-toolbar-select ap-notif-type-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filtrer par type"
          >
            {TYPE_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ap-notif-list-wrap">
          {loading ? (
            <div className="ap-notif-state">
              <Spinner animation="border" variant="primary" size="sm" />
              <p>{N.LOADING}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="ap-notif-state">
              <div className="ap-notif-state__icon">
                <Icon name="bell" size={28} />
              </div>
              <h3>{readFilter === 'unread' ? N.EMPTY_UNREAD_TITLE : N.EMPTY_TITLE}</h3>
              <p>{readFilter === 'unread' ? N.EMPTY_UNREAD_SUB : N.EMPTY_SUB}</p>
            </div>
          ) : (
            <ul className="ap-notif-list">
              {items.map((notification) => {
                const icon = getNotificationIcon(notification.type);
                const navTarget = getNotificationNavigateTarget(notification);
                const actionLabel = getNotificationActionLabel(notification);

                return (
                  <li
                    key={notification.id}
                    className={`ap-notif-item ${notification.read ? '' : 'is-unread'}`}
                  >
                    {!notification.read && <span className="ap-notif-item__dot" aria-hidden />}
                    <span className={`ap-notif-item__icon ap-notif-item__icon--${notification.type}`}>
                      <Icon name={icon} size={18} />
                    </span>
                    <div className="ap-notif-item__body">
                      <div className="ap-notif-item__head">
                        <h4>{notification.title}</h4>
                        <time dateTime={notification.timestamp}>
                          {formatNotificationTime(notification.timestamp)}
                        </time>
                      </div>
                      {notification.message && (
                        <p className="ap-notif-item__message">{notification.message}</p>
                      )}
                      <div className="ap-notif-item__actions">
                        {navTarget && actionLabel && (
                          <button
                            type="button"
                            className="ap-notif-link-btn"
                            onClick={() => navigate(navTarget)}
                          >
                            {actionLabel}
                            <Icon name="chevronRight" size={12} />
                          </button>
                        )}
                        {!notification.read && (
                          <button
                            type="button"
                            className="ap-notif-mark-btn"
                            onClick={() => handleMarkAsRead(notification.id)}
                          >
                            <Icon name="check" size={12} />
                            {N.BTN_MARK_ONE}
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ap-notif-delete-btn"
                      onClick={() => handleDelete(notification.id)}
                      disabled={deletingId === notification.id}
                      title={N.BTN_DELETE}
                      aria-label={N.BTN_DELETE}
                    >
                      {deletingId === notification.id ? (
                        <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                      ) : (
                        <Icon name="trash" size={16} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {!loading && total > 0 && (
          <footer className="ap-mem-footer ap-notif-footer">
            <span>
              {N.PAGINATION_LABEL(offset + 1, Math.min(offset + PAGE_SIZE, total), total)}
            </span>
            {totalPages > 1 && (
              <nav className="ap-mem-page-nav" aria-label="Pagination notifications">
                <button
                  type="button"
                  className="ap-mem-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <Icon name="chevronLeft" size={14} />
                </button>
                <span className="ap-mem-page-label">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="ap-mem-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <Icon name="chevronRight" size={14} />
                </button>
              </nav>
            )}
          </footer>
        )}
      </section>
    </div>
  );
}
